
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const AuthCallbackPage = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.log("Auth callback received. Processing...");
        // Extract the tokens from URL parameters
        const params = new URLSearchParams(location.search);
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const userId = params.get("user_id");
        const expiresAt = params.get("expires_at");
        
        if (!accessToken) {
          throw new Error("No access token found in URL");
        }
        
        // Debug information - will be displayed only in error state
        setDebugInfo(`Found tokens: access_token=${accessToken?.substring(0, 10)}..., 
                      refresh_token=${refreshToken ? refreshToken.substring(0, 5) + '...' : 'missing'},
                      user_id=${userId || 'missing'},
                      expires_at=${expiresAt || 'missing'}`);
        
        console.log("Setting session with tokens");
        // Set the session with the provided tokens
        const { data, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken || "",
        });
        
        if (sessionError) {
          console.error("Session error:", sessionError);
          throw new Error(`Failed to set session: ${sessionError.message}`);
        }
        
        // Verify the session was properly set
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          throw new Error("Session could not be established after setting tokens");
        }
        
        console.log("Authentication successful, redirecting to dashboard");
        // Redirect to the main page
        navigate("/");
      } catch (err: any) {
        console.error("Authentication error:", err);
        setError(err.message || "Failed to authenticate");
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [location, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-primary mx-auto" />
          <p className="mt-4 text-brand-dark">Setting up your session...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <Alert variant="destructive" className="mb-6">
            <AlertTitle className="text-xl">Authentication Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            {debugInfo && (
              <div className="mt-4 text-xs text-left bg-gray-100 p-2 rounded">
                <p className="font-semibold">Debug Info:</p>
                <pre className="whitespace-pre-wrap overflow-auto">{debugInfo}</pre>
              </div>
            )}
          </Alert>
          <Button 
            onClick={() => window.location.href = "https://auth.venturecatalysts.co.uk/login"}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            Return to Auth Portal
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallbackPage;
